import { Clock, Grid3X3, Trophy, Calendar, MapPin, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTournamentDateTime, TournamentConfig } from "@/services/tournamentService";

interface TournamentDetailsProps {
  tournamentDate?: Date | null;
  tournamentConfig?: TournamentConfig;
}

export const TournamentDetails = ({ tournamentDate: dateProp, tournamentConfig }: TournamentDetailsProps) => {
  const { t, language } = useLanguage();
  
  // Config must be passed in (no more sync getTournament() fallback)
  if (!tournamentConfig) return null;
  
  const config = tournamentConfig;
  const tournamentDate = dateProp || getTournamentDateTime(config);

  function formatTournamentTime(startTime: Date | null, durationMinutes: number = 90): string {
    if (!startTime) return "Ukjent tid";
    const options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
    const startFormatted = startTime.toLocaleTimeString("no-NO", options);
    const endTime = new Date(startTime);
    endTime.setMinutes(startTime.getMinutes() + durationMinutes);
    const endFormatted = endTime.toLocaleTimeString("no-NO", options);
    return `${startFormatted} - ${endFormatted}`;
  }
  
  const formatDateWithCapitalizedWeekday = (date: Date | null, lang: string = "no-NO"): string => {
    if (!date) return "Ukjent dato";
    const formattedDate = date.toLocaleDateString(lang, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  };

  if (!tournamentDate || isNaN(tournamentDate.getTime())) {
    return <p>Error: Invalid tournament date.</p>;
  }

  const startTime = format(tournamentDate, "yyyyMMdd'T'HHmmss");
  const endTime = format(new Date(tournamentDate.getTime() + config.duration * 60000), "yyyyMMdd'T'HHmmss");
  const googleCalendarLink = `https://www.google.com/calendar/render?action=TEMPLATE&text=Padel+Sosial+Turnering&dates=${startTime}%2F${endTime}&details=Padel+Tournament&trp=false&ctz=Europe/Oslo&location=${encodeURIComponent(config.location)}`;

  const courtNumbers = Array.isArray(config.courts) ? config.courts.join(', ') : String(config.courts);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <p className="text-lg">
              <span className="font-bold">{t("tournament.date")}</span>{" "}
              {formatDateWithCapitalizedWeekday(tournamentDate, language)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <p className="text-lg">
              <span className="font-bold">{t("tournament.time")}</span>{" "}
              {formatTournamentTime(tournamentDate, config.duration)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="whitespace-nowrap" asChild>
            <a href={googleCalendarLink} target="_blank" rel="noopener noreferrer">
              <Calendar className="mr-2 h-4 w-4" />
              Google Calendar
            </a>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-primary" />
        <p className="text-lg">
          <span className="font-bold">{t("tournament.location")}</span>{" "}
          <span>{config.location}</span>
          {config.locationUrl && (
            <Button variant="link" className="p-0 h-auto ml-2" asChild>
              <a href={config.locationUrl} target="_blank">({t("map")})</a>
            </Button>
          )}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Grid3X3 className="h-5 w-5 text-primary" />
        <p className="text-lg">
          <span className="font-bold">{t("tournament.courts")}</span>{" "}
          <span>{courtNumbers}</span>
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Trophy className="h-5 w-5 text-primary" />
        <p className="text-lg">
          <span className="font-bold">{t("tournament.level")}</span>{" "}
          <span>{config.level}</span>
        </p>
      </div>

      {config.doorCode && (
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <p className="text-lg">
            <span className="font-bold">{language === 'no' ? 'Dørkode:' : 'Door code:'}</span>{" "}
            <span>{config.doorCode}</span>
          </p>
        </div>
      )}
    </div>
  );
};
